import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle,
  Loader2,
  XCircle,
  Play,
  FileCheck,
  AlertCircle,
} from "lucide-react";
import { UserProject, UserProjectStatus } from "../../lib/api/projects";

interface ProjectCardProps {
  userProject: UserProject;
  variant: "available" | "pending" | "my_projects";
}

const ProjectCard: React.FC<ProjectCardProps> = ({ userProject, variant }) => {
  const navigate = useNavigate();
  const project = userProject.project;

  const getCategoryConfig = (category: string) => {
    const configs: Record<
      string,
      { bg: string; text: string; border: string }
    > = {
      "AI Training": {
        bg: "bg-blue-100",
        text: "text-blue-700",
        border: "border-blue-200",
      },
      "Data Annotation": {
        bg: "bg-emerald-100",
        text: "text-emerald-700",
        border: "border-emerald-200",
      },
      "Code Review": {
        bg: "bg-purple-100",
        text: "text-purple-700",
        border: "border-purple-200",
      },
      "Content Moderation": {
        bg: "bg-amber-100",
        text: "text-amber-700",
        border: "border-amber-200",
      },
      "Audio Transcription": {
        bg: "bg-rose-100",
        text: "text-rose-700",
        border: "border-rose-200",
      },
      "Sentiment Analysis": {
        bg: "bg-cyan-100",
        text: "text-cyan-700",
        border: "border-cyan-200",
      },
      "Prompt Engineering": {
        bg: "bg-indigo-100",
        text: "text-indigo-700",
        border: "border-indigo-200",
      },
      "Image Classification": {
        bg: "bg-teal-100",
        text: "text-teal-700",
        border: "border-teal-200",
      },
      "Text Review": {
        bg: "bg-orange-100",
        text: "text-orange-700",
        border: "border-orange-200",
      },
      "Video Annotation": {
        bg: "bg-pink-100",
        text: "text-pink-700",
        border: "border-pink-200",
      },
    };
    return (
      configs[category] || {
        bg: "bg-gray-100",
        text: "text-gray-700",
        border: "border-gray-200",
      }
    );
  };

  const getStatusConfig = (status: UserProjectStatus) => {
    const configs: Record<
      UserProjectStatus,
      {
        bg: string;
        border: string;
        text: string;
        icon: React.ElementType;
        label: string;
        animate?: boolean;
      }
    > = {
      assigned: {
        bg: "bg-blue-50",
        border: "border-blue-200",
        text: "text-blue-700",
        icon: AlertCircle,
        label: "Available to Apply",
      },
      pending: {
        bg: "bg-amber-50",
        border: "border-amber-200",
        text: "text-amber-700",
        icon: Loader2,
        label: "Verification Pending",
        animate: true,
      },
      approved: {
        bg: "bg-emerald-50",
        border: "border-emerald-200",
        text: "text-emerald-700",
        icon: CheckCircle,
        label: "Approved - Ready to Start",
      },
      active: {
        bg: "bg-blue-50",
        border: "border-blue-200",
        text: "text-blue-700",
        icon: Play,
        label: "Active",
      },
      completed: {
        bg: "bg-emerald-50",
        border: "border-emerald-200",
        text: "text-emerald-700",
        icon: FileCheck,
        label: "Completed",
      },
      rejected: {
        bg: "bg-red-50",
        border: "border-red-200",
        text: "text-red-700",
        icon: XCircle,
        label: "Rejected",
      },
      terminated: {
        bg: "bg-gray-100",
        border: "border-gray-200",
        text: "text-gray-700",
        icon: XCircle,
        label: "Terminated",
      },
    };
    return configs[status] || configs.assigned;
  };

  if (!project) {
    return null;
  }

  const categoryConfig = getCategoryConfig(project.category);
  const statusConfig = getStatusConfig(userProject.status);
  const StatusIcon = statusConfig.icon;

  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getDateLabel = () => {
    switch (userProject.status) {
      case "assigned":
        return userProject.assignedAt
          ? `Assigned ${formatDate(userProject.assignedAt)}`
          : "";
      case "pending":
        return userProject.appliedAt
          ? `Applied ${formatDate(userProject.appliedAt)}`
          : "";
      case "approved":
        return userProject.approvedAt
          ? `Approved ${formatDate(userProject.approvedAt)}`
          : "";
      case "active":
        return userProject.startedAt
          ? `Started ${formatDate(userProject.startedAt)}`
          : "";
      case "completed":
        return userProject.completedAt
          ? `Completed ${formatDate(userProject.completedAt)}`
          : "";
      case "rejected":
        return userProject.rejectedAt
          ? `Rejected ${formatDate(userProject.rejectedAt)}`
          : "";
      case "terminated":
        return userProject.terminatedAt
          ? `Terminated ${formatDate(userProject.terminatedAt)}`
          : "";
      default:
        return "";
    }
  };

  const handleViewClick = () => {
    navigate(`/projects/${userProject._id}`);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all duration-200 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="p-4 pb-0">
        <div className="flex items-start justify-between gap-3 mb-3">
          <span
            className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${categoryConfig.bg} ${categoryConfig.text}`}
          >
            {project.category.toUpperCase()}
          </span>
          <span className="text-lg font-bold text-gray-900">
            ${project.hourlyRate}/hr
          </span>
        </div>

        <h3
          onClick={handleViewClick}
          className="font-semibold text-gray-900 text-lg mb-2 line-clamp-1"
        >
          {project.title}
        </h3>
        <p className="text-sm text-gray-500 line-clamp-3 mb-4">
          {project.description}
        </p>
      </div>

      {/* Meta Info */}
      <div className="px-4 pb-4 flex-1">
        {/* Status Badge - Show for pending and my_projects variants */}
        {variant !== "available" && (
          <div
            className={`flex items-center gap-2 p-2.5 rounded-lg ${statusConfig.bg} border ${statusConfig.border}`}
          >
            <StatusIcon
              className={`w-4 h-4 ${statusConfig.text} ${statusConfig.animate ? "animate-spin" : ""}`}
            />
            <div className="flex-1">
              <span className={`text-sm font-medium ${statusConfig.text}`}>
                {statusConfig.label}
              </span>
              {getDateLabel() && (
                <p className="text-xs text-gray-500 mt-0.5">{getDateLabel()}</p>
              )}
            </div>
          </div>
        )}

        {/* Rejection/Termination Reason */}
        {userProject.status === "rejected" && userProject.rejectionReason && (
          <p className="text-xs text-red-600 mt-2 line-clamp-2">
            Reason: {userProject.rejectionReason}
          </p>
        )}
        {userProject.status === "terminated" &&
          userProject.terminationReason && (
            <p className="text-xs text-gray-600 mt-2 line-clamp-2">
              Reason: {userProject.terminationReason}
            </p>
          )}
      </div>

      {/* Footer */}
      {variant === "available" && (
        <div className="px-4 pb-4">
          <button
            onClick={handleViewClick}
            className="w-full py-3 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/30 hover:-translate-y-0.5"
          >
            View Task
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}
      {variant === "pending" && (
        <div className="px-4 pb-4">
          <button
            onClick={handleViewClick}
            className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
          >
            View Task
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {variant === "pending" && (
        <div className="px-4 pb-4">
          <p className="text-gray-500 italic font-light text-xs">
            Your application is being reviewed. We'll notify you once approved.
          </p>
        </div>
      )}

      {variant === "my_projects" && (
        <div className="px-4 pb-4">
          {userProject.status === "approved" && (
            <button
              onClick={handleViewClick}
              className="w-full cursor-pointer py-3 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/30 hover:-translate-y-0.5"
            >
              Start Working
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
          {userProject.status === "active" && (
            <button
              onClick={handleViewClick}
              className="w-full py-3 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center gap-2 bg-gradient-to-r from-[#8B52FF] to-[#E05A6A] text-white shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/30 hover:-translate-y-0.5"
            >
              Continue Working
              <ArrowRight className="w-4 h-4" />
            </button>
          )}
          {userProject.status === "completed" && (
            <p className="text-gray-500 italic font-light text-xs">
              Great job! This project has been completed.
            </p>
          )}
          {userProject.status === "rejected" && (
            <p className="text-gray-500 italic font-light text-xs">
              Your application was not approved. You may apply to other
              available projects.
            </p>
          )}
          {userProject.status === "terminated" && (
            <p className="text-gray-500 italic font-light text-xs">
              This project has been terminated. Contact support for more
              information.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectCard;
